import React, { useMemo } from 'react';
import { useIsDesktop, useIsLandscapePhone } from '../../hooks/useIsDesktop';
import { DEGREE_LABELS, buildArpeggioNotes } from '../../lib/theory';
import {
  buildFFcPNodes,
  buildChopChordNodes,
  buildDoubleStopNodes,
  MANDOLIN_INLAY_FRETS,
  MANDOLIN_DOUBLE_INLAY_FRETS,
  TOTAL_FRETS_MANDOLIN,
} from '../../lib/mandolin-theory';

/** Open strings low to high as note names, for the shared arpeggio builder in theory.js. */
const MANDOLIN_TUNING_NAMES = ['G', 'D', 'A', 'E'];

// ── Layout (boards 18 + 20, same look as the guitar) ──────────────────────────
//
// The SVG scales to its container, so every number below is a viewBox unit.
//
// PHONE is authored 1:1 with board 18: rendered at the board's 358px width the
// viewBox scale is ~1, so strings land 28px apart and dots are 21px (75% of the gap).
// Frets 0-12, equal-width frets (~26px each; the old taper crushed frets 8-12).
//
// DESKTOP is board 20: frets 0-15, equal-width frets (same as the phone),
// 36px dots on a 49.2px string gap (73%). The board is 880 wide so a typical main-area
// width (~900px) renders it near 1:1; it scales to fill any width.

const NUM_STRINGS = 4;
// String names low to high (G D A E); string 0 sits at the bottom of the board.
const OPEN_STRING_NAMES = ['G', 'D', 'A', 'E'];

const PHONE = {
  frets: 12,
  left: 16,          // strings start here; the string names sit in the 0-12 gutter
  boardWidth: 338,   // fret 0 slot through the end of fret 12
  gap: 28,
  pad: 12,
  nutW: 4,
  noteR: 10.5,       // outer radius (21px dot incl. ring), 75% of the 28 string gap
  ring: 1.25,
  font: 9,
  fontSmall: 8,      // labels longer than 2 chars (e.g. "bb7")
  inlayR: 4,
  stringThick: [2.4, 2, 1.5, 1.1],
  nameX: 6,
  nameFont: 11,
  numFont: 11,
  numTop: 3,         // gap between the board bottom and the fret-number line
  numLine: 15,
  bottom: 20,        // room under the board for fret numbers
  viewLeft: 0,
  viewRightPad: 2,
};

// LANDSCAPE is a phone held sideways (useIsLandscapePhone): frets 0-15 like the desktop, equal
// frets (~52px) and a ~34px string gap (roomier than the phone, for bigger dots), so the whole
// neck fits in ~215px of height and the board needs no scrolling. Authored ~1:1 at a typical 812px-wide landscape screen.
const LANDSCAPE = {
  ...PHONE,
  frets: 15,
  boardWidth: 800,
  gap: 34,
  noteR: 13,         // 26px dot incl. ring, 76% of the 34 string gap
  font: 11,
  fontSmall: 10,
  inlayR: 4.5,
  nameFont: 12,
  numFont: 12,
};

const DESKTOP = {
  frets: 15,
  left: 0,
  boardWidth: 880,
  gap: 49.2,
  pad: 27,
  nutW: 5,
  noteR: 18,         // 36px dot incl. ring, 73% of the gap
  ring: 1.25,
  font: 13,
  fontSmall: 11,
  inlayR: 5,
  stringThick: [3, 2.5, 1.9, 1.3],
  nameX: -11,
  nameFont: 13,
  numFont: 13,
  numTop: 10,
  numLine: 18,
  bottom: 30,
  viewLeft: -22,     // gutter for the open-string names
  viewRightPad: 0,
};

/**
 * Slot widths (viewBox units) for fret 0..frets. Slot 0 is the open-string zone left of the nut.
 * Every slot is the same width, on every layout (Oct 2026): a real-neck taper squeezed the high
 * frets narrower than the dots on phones, and desktop was switched to match.
 */
function fretWidths(L) {
  return Array.from({ length: L.frets + 1 }, () => L.boardWidth / (L.frets + 1));
}

function fretPositions(widths, left) {
  const positions = [left];
  for (let i = 1; i < widths.length; i++) {
    positions.push(positions[i - 1] + widths[i - 1]);
  }
  return positions;
}

// ── State translation helpers ─────────────────────────────────────────────────

/** Translate shape display name → theory-engine name expected by builder fns */
const SHAPE_NAME_MAP = {
  '1st FINGER':  '1st FINGER',
  '2nd FINGER':  '2nd FINGER',
  '3rd FINGER':  '3rd FINGER',
  '4th FINGER':  '4th FINGER',
  'G Shape':     'G SHAPE',
  'C Shape':     'C SHAPE',
  'A Shape':     'A SHAPE',
  'D Shape':     'D SHAPE',
  'Short Shape': 'SHORT SHAPE',
  'Long Shape':  'LONG SHAPE',
  'All Shapes':  null,  // null = no positional window (full neck)
};

/**
 * Derive the legacy `activeNav` value the builder functions expect.
 *   Structure 'Chop Chords'  → 'CHOP CHORDS'
 *   Structure 'Double Stops' → 'DOUBLE STOPS'
 *   System    'FFcP'         → 'FFcP'
 *   Otherwise                → 'Standard Scales'
 */
function deriveNav(structure, system) {
  if (structure === 'Chop Chords')  return 'CHOP CHORDS';
  if (structure === 'Double Stops') return 'DOUBLE STOPS';
  if (system    === 'FFcP')         return 'FFcP';
  return 'Standard Scales';
}

/**
 * Derive the legacy `activeMode` string the builder functions expect.
 *   'Double Stops' → shape holds the interval name ('3rds' | '4ths' | '5ths' | '6ths')
 *   'Chop Chords'  → null (unused by buildChopChordNodes)
 *   'Pentatonic'   → 'PENTATONIC'
 *   'Modes'        → uppercase mode name (e.g. 'IONIAN', 'DORIAN')
 *   'Full Scale'   → 'MAJOR SCALE' (tonality drives maj vs natural-minor inside builder)
 */
function deriveMode(structure, shape, modeName) {
  if (structure === 'Double Stops') return shape;        // interval stored in shape
  if (structure === 'Chop Chords')  return null;
  if (structure === 'Pentatonic')   return 'PENTATONIC';
  if (structure === 'Modes')        return modeName.toUpperCase();
  return 'MAJOR SCALE';
}

/**
 * Derive the positional `activeShape` value the builder functions expect.
 *   'Double Stops' → always 'SHORT SHAPE' (interval lives in activeMode, not here)
 *   Otherwise      → look up through SHAPE_NAME_MAP
 */
function deriveShape(structure, shape) {
  if (structure === 'Double Stops') return 'SHORT SHAPE';
  return SHAPE_NAME_MAP[shape] ?? null;
}

/** Map new filter label → legacy filterOption used in node filter logic */
function mapFilter(filter) {
  if (filter === 'Triads Only') return 'Triads';
  if (filter === 'Roots Only')  return 'Root Notes';
  return 'All Notes';
}

/** Map new label string → legacy labelOption used in noteLabel() */
function mapLabel(label) {
  if (label === 'Intervals')  return 'Scale Degrees';
  if (label === 'Note Names') return 'Note Names';
  return '';
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function MandolinFretboard({
  mandolinKey,
  mandolinTonality,   // 'Major' | 'Minor'
  mandolinStructure,  // 'Full Scale' | 'Pentatonic' | 'Modes' | 'Arpeggios' (Chop Chords / Double Stops are hidden but still supported)
  mandolinMode,       // diatonic mode name, used when mandolinStructure === 'Modes'
  mandolinSystem,
  mandolinShape,
  mandolinStringFocus,
  mandolinFilter,
  mandolinLabel,
  mandolinArpeggioType, // used when mandolinStructure === 'Arpeggios'
}) {
  const isDesktop = useIsDesktop();
  const isLandscape = useIsLandscapePhone();
  const L = isDesktop ? DESKTOP : isLandscape ? LANDSCAPE : PHONE;
  const DISPLAY_FRETS = L.frets;
  const tonalityLower = mandolinTonality.toLowerCase(); // 'major' | 'minor'

  // Translate new state → legacy builder parameters
  const activeNav   = deriveNav(mandolinStructure, mandolinSystem);
  const activeMode  = deriveMode(mandolinStructure, mandolinShape, mandolinMode);
  const activeShape = deriveShape(mandolinStructure, mandolinShape);
  const filterOpt   = mapFilter(mandolinFilter);
  const labelOpt    = mapLabel(mandolinLabel);

  const isArpeggios = mandolinStructure === 'Arpeggios';

  // Build raw nodes from the theory engine
  const activeNodes = useMemo(() => {
    if (isArpeggios) {
      // Full-neck map of every chord tone. `interval` here is already the degree label ('b3', '7').
      return buildArpeggioNotes({
        key: mandolinKey, type: mandolinArpeggioType, tonality: mandolinTonality,
        tuning: MANDOLIN_TUNING_NAMES, stringFocus: mandolinStringFocus,
        totalFrets: TOTAL_FRETS_MANDOLIN,
      }).map(n => ({ ...n, degreeLabel: n.interval }));
    }
    if (activeNav === 'Standard Scales') {
      return buildFFcPNodes(mandolinKey, activeMode, tonalityLower, mandolinStringFocus, null);
    }
    if (activeNav === 'FFcP') {
      return buildFFcPNodes(mandolinKey, activeMode, tonalityLower, mandolinStringFocus, activeShape);
    }
    if (activeNav === 'CHOP CHORDS') {
      return buildChopChordNodes(mandolinKey, activeShape, tonalityLower, mandolinStringFocus);
    }
    // DOUBLE STOPS — activeMode = interval name ('3rds'…), activeShape = 'SHORT SHAPE'
    return buildDoubleStopNodes(mandolinKey, activeMode, tonalityLower, mandolinStringFocus, activeShape);
  }, [isArpeggios, mandolinKey, mandolinTonality, mandolinArpeggioType, tonalityLower, activeNav, activeMode, activeShape, mandolinStringFocus]);

  // Set of string indices that are muted in the current chop chord voicing.
  // Used to render ✕ markers in place of open-string note names.
  const mutedStrings = useMemo(() => {
    if (activeNav !== 'CHOP CHORDS') return new Set();
    return new Set(activeNodes.filter(n => n.isMuted).map(n => n.string));
  }, [activeNodes, activeNav]);

  // Apply display filter (chop chord voicings and arpeggios always shown in full)
  const visibleNodes = useMemo(() => {
    if (activeNav === 'CHOP CHORDS' || isArpeggios) return activeNodes;
    return activeNodes.filter(node => {
      if (filterOpt === 'Root Notes') return node.isRoot;
      if (filterOpt === 'Triads')     return [0, 3, 4, 7].includes(node.interval);
      return true;
    });
  }, [activeNodes, filterOpt, activeNav, isArpeggios]);

  // Geometry
  const widths     = useMemo(() => fretWidths(L), [L]);
  const xPositions = useMemo(() => fretPositions(widths, L.left), [widths, L]);
  const right      = xPositions[DISPLAY_FRETS] + widths[DISPLAY_FRETS]; // end of the last fret
  const nutX       = xPositions[1];

  const neckH = L.pad * 2 + (NUM_STRINGS - 1) * L.gap;
  const midY  = neckH / 2;
  const viewW = right + L.viewRightPad - L.viewLeft;
  const viewH = neckH + L.bottom;

  const noteX   = (fretIdx) => xPositions[fretIdx] + widths[fretIdx] / 2;
  const stringY = (sIdx) => L.pad + (NUM_STRINGS - 1 - sIdx) * L.gap;

  const noteLabel = (node) => {
    if (labelOpt === 'Scale Degrees') return node.degreeLabel ?? DEGREE_LABELS[node.interval] ?? '';
    if (labelOpt === 'Note Names')    return node.noteName;
    return '';
  };

  const inlayFrets = MANDOLIN_INLAY_FRETS.filter(f => f <= DISPLAY_FRETS);

  // One note dot: roots are solid terracotta with a white label; scale tones are
  // white with a sage ring and an ink label. Flat — the boards have no shadow.
  const renderDot = (node) => {
    const cx = noteX(node.fret);
    const cy = stringY(node.string);
    const label = noteLabel(node);
    return (
      <g
        key={`mando-note-${node.string}-${node.fret}-${mandolinKey}-${mandolinStructure}-${mandolinTonality}-${isArpeggios ? mandolinArpeggioType : ''}`}
        className="animate-note-spring"
        style={{ animationDelay: `${node.fret * 15}ms` }}
      >
        <circle
          cx={cx} cy={cy} r={L.noteR - L.ring / 2}
          strokeWidth={L.ring}
          style={{
            fill:   node.isRoot ? 'hsl(var(--root))' : 'hsl(var(--note-scale-fill))',
            stroke: node.isRoot ? 'hsl(var(--root))' : 'hsl(var(--sage))',
          }}
        />
        {label !== '' && label != null && (
          <text
            x={cx} y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={String(label).length > 2 ? L.fontSmall : L.font}
            fontWeight={800}
            className="pointer-events-none"
            style={{ fill: node.isRoot ? 'hsl(var(--primary-foreground))' : 'hsl(var(--ink))' }}
          >
            {label}
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="w-full">
      <svg
        viewBox={`${L.viewLeft} 0 ${viewW} ${viewH}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        className="select-none"
      >
        {/* Board surface with hairline top/bottom edges */}
        <rect x={nutX} y={0} width={right - nutX} height={neckH} style={{ fill: 'hsl(var(--board))' }} />
        <rect x={nutX} y={0} width={right - nutX} height={1} style={{ fill: 'hsl(var(--board-edge))' }} />
        <rect x={nutX} y={neckH - 1} width={right - nutX} height={1} style={{ fill: 'hsl(var(--board-edge))' }} />

        {/* Inlay dots */}
        {inlayFrets.map(f => {
          const cx = noteX(f);
          const dotStyle = { fill: 'hsl(var(--inlay))' };
          if (MANDOLIN_DOUBLE_INLAY_FRETS.includes(f)) {
            return (
              <g key={`inlay-${f}`}>
                <circle cx={cx} cy={midY - L.gap} r={L.inlayR} style={dotStyle} />
                <circle cx={cx} cy={midY + L.gap} r={L.inlayR} style={dotStyle} />
              </g>
            );
          }
          return <circle key={`inlay-${f}`} cx={cx} cy={midY} r={L.inlayR} style={dotStyle} />;
        })}

        {/* Nut — solid ink, flush against the left of fret 1 */}
        <rect x={nutX - L.nutW} y={0} width={L.nutW} height={neckH} style={{ fill: 'hsl(var(--string))' }} />

        {/* Fret wires — thin grey; the last one closes the end of the board */}
        {Array.from({ length: DISPLAY_FRETS - 1 }, (_, i) => i + 2).concat(DISPLAY_FRETS + 1).map(f => {
          const x = f > DISPLAY_FRETS ? right : xPositions[f];
          return (
            <rect
              key={`fret-${f}`}
              x={x - 0.5} y={0} width={1} height={neckH}
              style={{ fill: 'hsl(var(--fret))' }}
            />
          );
        })}

        {/* Strings — solid ink, thicker toward the low G; muted strings fade */}
        {OPEN_STRING_NAMES.map((_, s) => {
          const thick = L.stringThick[s] ?? 1.5;
          return (
            <rect
              key={`string-${s}`}
              x={L.left} y={stringY(s) - thick / 2}
              width={right - L.left} height={thick}
              style={{ fill: 'hsl(var(--string))' }}
              opacity={mandolinStringFocus[s] ? 1 : 0.2}
            />
          );
        })}

        {/* Open-string names — left of the nut, clear of the open-string note dots.
            A string that is muted in the current chop-chord voicing shows ✕ instead. */}
        {OPEN_STRING_NAMES.map((name, s) => {
          const isMuted = mutedStrings.has(s);
          return (
            <text
              key={`string-label-${s}`}
              x={L.nameX}
              y={stringY(s)}
              textAnchor="middle"
              dominantBaseline="central"
              style={{ fill: isMuted ? 'hsl(var(--root))' : 'hsl(var(--ink-muted))' }}
              fontSize={L.nameFont}
              fontWeight={700}
            >
              {isMuted ? '✕' : name}
            </text>
          );
        })}

        {/* Note markers — skip muted chop-chord strings (rendered as ✕ labels instead) */}
        {visibleNodes
          .filter(node => !node.isMuted && node.fret <= DISPLAY_FRETS)
          .map(renderDot)}

        {/* Fret numbers — under the board; inlay frets are bold ink, the rest muted */}
        {Array.from({ length: DISPLAY_FRETS + 1 }, (_, i) => i).map(f => {
          const marked = inlayFrets.includes(f);
          return (
            <text
              key={`fnum-${f}`}
              x={noteX(f)} y={neckH + L.numTop + L.numLine / 2}
              textAnchor="middle"
              dominantBaseline="central"
              style={{ fill: marked ? 'hsl(var(--ink))' : 'hsl(var(--ink-muted))' }}
              fontSize={L.numFont}
              fontWeight={marked ? 800 : 600}
              opacity={marked ? 1 : 0.75}
              className="select-none"
            >
              {f}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
